import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  deleteTemplate,
  templatesKeys,
  templatesQueryOptions,
  type IMalformedTemplate,
  tagsByGroupQueryOptions,
  type IBatchTemplate,
  type IActivityTemplate,
  type ITemplateTag,
  type IActivityTemplateWithPatient,
  type IActivityTemplateWithoutPatient,
} from "@/api/Templates";
import { Loading } from "@medoma/components/Loading/Loading";
import { deducedError } from "@/Utils/ErrorUtils";
import { Trans, useLingui } from "@lingui/react/macro";
import ErrorMessage from "@medoma/components/ErrorMessage/ErrorMessage";
import { Text, TextWithEllipsis } from "@medoma/components/Text/Text";
import { CategoryIcon } from "@/components/CategoryIcon/CategoryIcon";
import { CompetenceChip } from "@/components/Chips/CompetenceChip";
import styles from "./TemplatesList.module.scss";
import { UnstyledLink, Link } from "@medoma/components/Link/Link";
import { isActivityTemplate, isBatchTemplate } from "@medoma/models/templates";
import {
  parseGroupPatientType,
  groupTemplatesByTag,
  type ITemplate,
  type IGroupedTemplates,
} from "./tagUtils";
import {
  sortActivityTemplates,
  sortBatchTemplates,
} from "./templateManagerUtils";
import FolderIcon from "@medoma/components/icons/FolderIcon";
import { Heading } from "@medoma/components/Heading/Heading";
import {
  patientTypeDictionary,
  type IPatientType,
} from "@medoma/models/patients";
import NoResults from "@/components/NoResults/NoResults";
import { PlainButton } from "@medoma/components/Button/Button";
import BinIcon from "@medoma/components/icons/BinIcon";

type IPatientTypeSection = {
  patientType: IPatientType;
  groups: IGroupedTemplates[];
  ungroupedTemplates: ITemplate[];
};

function splitTemplatesByPatientType(
  activities: IActivityTemplateWithPatient[],
  batches: IBatchTemplate[],
  allGroupTags: ITemplateTag[],
  availablePatientTypes: readonly IPatientType[],
  matchesCompetenceFilter?: (template: ITemplate) => boolean,
): IPatientTypeSection[] {
  const allTemplates: (IActivityTemplateWithPatient | IBatchTemplate)[] = [
    ...batches,
    ...activities,
  ].filter(
    (template) => !matchesCompetenceFilter || matchesCompetenceFilter(template),
  );

  const sections: IPatientTypeSection[] = [];

  // Only iterate over available patient types
  for (const patientType of availablePatientTypes) {
    const templatesForType = allTemplates.filter(
      (template) => template.template.patientType === patientType,
    );
    const groupTagsForType = allGroupTags.filter(
      (tag) => parseGroupPatientType(tag.displayName) === patientType,
    );
    const { groups, ungroupedTemplates } = groupTemplatesByTag(
      templatesForType,
      groupTagsForType,
    );
    sections.push({ patientType, groups, ungroupedTemplates });
  }

  return sections;
}

type TemplatesListProps = {
  patientTypes: IPatientType[];
  matchesCompetenceFilter?: (template: ITemplate) => boolean;
};

const TemplatesList = ({
  patientTypes,
  matchesCompetenceFilter,
}: TemplatesListProps) => {
  const {
    data: templates,
    isPending: isPendingTemplates,
    isError: isErrorTemplates,
    error: errorTemplates,
  } = useQuery(templatesQueryOptions());

  const {
    data: groupTags,
    isPending: isPendingGroupTags,
    isError: isErrorGroupTags,
    error: errorGroupTags,
  } = useQuery(tagsByGroupQueryOptions("group"));

  const { t } = useLingui();

  if (isPendingTemplates || isPendingGroupTags) {
    return <Loading message={t`Hämtar mallar`} />;
  }

  if (isErrorTemplates) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta mallar.`} ${deducedError(errorTemplates)}`}
      />
    );
  }

  if (isErrorGroupTags) {
    return (
      <ErrorMessage
        message={`${t`Kunde inte hämta grupper.`} ${deducedError(errorGroupTags)}`}
      />
    );
  }

  const { activities, batches, malformedTemplates } = templates;
  const {
    withPatient: activitiesWithPatient,
    withoutPatient: activitiesWithoutPatient,
  } = activities;

  const sections = splitTemplatesByPatientType(
    activitiesWithPatient,
    batches,
    groupTags,
    patientTypes,
    matchesCompetenceFilter,
  );

  const hasMultiplePatientTypes = patientTypes.length > 1;

  return (
    <div className={styles.templatesList}>
      {malformedTemplates.length > 0 ? (
        <div className={styles.malformedTemplates}>
          <Heading level="h2">{t`Felaktiga mallar`}</Heading>
          <Text element="span">{t`Följande mallar är felaktigt konfigurerade och kan inte användas. Kontakta support för att få hjälp med att åtgärda dessa mallar.`}</Text>
          <ul className={styles.malformedTemplateItems}>
            {malformedTemplates.map((malformedTemplate) => {
              const serialized = JSON.stringify(malformedTemplate, null, 2);
              return (
                <MalformedTemplateItem
                  key={serialized}
                  malformedTemplate={malformedTemplate}
                  serialized={serialized}
                />
              );
            })}
          </ul>
        </div>
      ) : null}
      {sections.map((section) => (
        <PatientTypeSection
          key={section.patientType}
          section={section}
          hasMultiplePatientTypes={hasMultiplePatientTypes}
        />
      ))}
      {activitiesWithoutPatient.length > 0 ? (
        <TemplatesWithoutPatientSection templates={activitiesWithoutPatient} />
      ) : null}
    </div>
  );
};

const MalformedTemplateItem = ({
  malformedTemplate,
  serialized,
}: {
  malformedTemplate: IMalformedTemplate;
  serialized: string;
}) => {
  const queryClient = useQueryClient();
  const { templateId } = malformedTemplate;

  const { mutate: deleteTemplateMutation, isPending: isPendingDelete } =
    useMutation({
      mutationFn: (id: string) => deleteTemplate(id),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: templatesKeys.all });
      },
    });

  return (
    <li className={styles.malformedTemplateItem}>
      <small>
        <pre>{serialized}</pre>
      </small>
      {typeof templateId === "string" ? (
        <PlainButton
          type="button"
          disabled={isPendingDelete}
          onClick={() => deleteTemplateMutation(templateId)}
        >
          <BinIcon />
          <Trans>Ta bort</Trans>
        </PlainButton>
      ) : null}
    </li>
  );
};

const SortedTemplateItems = ({ templates }: { templates: ITemplate[] }) => {
  const batches = templates.filter(isBatchTemplate);
  const activities = templates.filter(isActivityTemplate);
  const sortedBatches = sortBatchTemplates([...batches]);
  const sortedActivities = sortActivityTemplates([...activities]);
  const sortedTemplates = [...sortedBatches, ...sortedActivities];
  return (
    <>
      {sortedTemplates.map((template) => (
        <li key={template.templateId} className={styles.templateItem}>
          {isActivityTemplate(template) ? (
            <ActivityTemplateItem activity={template} />
          ) : (
            <BatchTemplateItem batch={template} />
          )}
        </li>
      ))}
    </>
  );
};

type PatientTypeSectionProps = {
  section: IPatientTypeSection;
  hasMultiplePatientTypes: boolean;
};

const PatientTypeSection = ({
  section,
  hasMultiplePatientTypes,
}: PatientTypeSectionProps) => {
  const { t } = useLingui();

  const hasContent =
    section.groups.length > 0 || section.ungroupedTemplates.length > 0;

  return (
    <section>
      {hasMultiplePatientTypes ? (
        <Heading level="h2">
          {t(patientTypeDictionary[section.patientType])}
        </Heading>
      ) : null}
      {hasContent ? (
        <>
          {section.groups.map((group) => (
            <TemplateGroup key={group.groupId} group={group} />
          ))}
          {section.ungroupedTemplates.length > 0 && (
            <ul className={styles.ungroupedTemplates}>
              <SortedTemplateItems templates={section.ungroupedTemplates} />
            </ul>
          )}
        </>
      ) : (
        <div className={styles.noResults}>
          <NoResults
            message={
              hasMultiplePatientTypes
                ? t`Inga mallar har skapats för denna patienttyp`
                : t`Inga mallar har skapats`
            }
            size="small"
          />
        </div>
      )}
    </section>
  );
};

const TemplatesWithoutPatientSection = ({
  templates,
}: {
  templates: IActivityTemplateWithoutPatient[];
}) => {
  const { t } = useLingui();

  return (
    <section>
      <Heading level="h2">
        <Trans>Utan patient</Trans>
      </Heading>

      {templates.length > 0 ? (
        <ul className={styles.ungroupedTemplates}>
          <SortedTemplateItems templates={templates} />
        </ul>
      ) : (
        <NoResults
          message={t`Inga mallar utan patient har skapats`}
          size="small"
        />
      )}
    </section>
  );
};

const TemplateGroup = ({ group }: { group: IGroupedTemplates }) => {
  const { t } = useLingui();
  const sortingNumber = group.sortingNumber;

  return (
    <div className={styles.group}>
      <div className={styles.groupHeader}>
        <FolderIcon />
        <Link to={`groups/${group.groupId}`} size="small" color="neutral">
          {group.groupName}
        </Link>
        {sortingNumber !== undefined && (
          <Text element="span" color="faded" size="small">
            {t`Sortering: ${sortingNumber}`}
          </Text>
        )}
      </div>
      <ul className={styles.groupItems}>
        <SortedTemplateItems templates={group.templates} />
      </ul>
    </div>
  );
};

const ActivityTemplateItem = ({
  activity,
}: {
  activity: IActivityTemplate;
}) => {
  const { template } = activity;
  const competences = template.requiredCompetences ?? [];

  return (
    <UnstyledLink
      key={activity.templateId}
      to={`activity-templates/${activity.templateId}`}
    >
      <div className={styles.templateItemContent}>
        <CategoryIcon category={template.category} size="tiny" />
        <TextWithEllipsis
          title={template.title}
          element="span"
          maxWidth="100%"
          size="small"
        >
          {template.title}
        </TextWithEllipsis>
        <div className={styles.competences}>
          {competences.map((competence) => (
            <CompetenceChip
              key={competence}
              competence={competence}
              state="neutral"
              size="tiny"
            />
          ))}
        </div>
      </div>
    </UnstyledLink>
  );
};

const BatchTemplateItem = ({ batch }: { batch: IBatchTemplate }) => {
  const { template } = batch;
  const competences = Array.from(
    new Set(
      template.activities.flatMap(
        (activity) => activity.requiredCompetences ?? [],
      ),
    ),
  );

  return (
    <UnstyledLink
      key={batch.templateId}
      to={`batch-templates/${batch.templateId}`}
    >
      <div className={styles.templateItemContent}>
        <CategoryIcon category={"MultipleCategories"} size="tiny" />
        <TextWithEllipsis
          title={template.title}
          element="span"
          maxWidth="100%"
          size="small"
        >
          {template.title}
        </TextWithEllipsis>
        <div className={styles.competences}>
          {competences.map((competence) => (
            <CompetenceChip
              key={competence}
              competence={competence}
              state="neutral"
              size="tiny"
            />
          ))}
        </div>
      </div>
    </UnstyledLink>
  );
};

export default TemplatesList;
